import { dayjs } from "@/core/utils/date";
import type { IAiSpreadsheetCell } from "./types";

type OptimisticCellValue = string | number | boolean | null;

const DATE_INPUT_FORMATS = [
  "M/D/YYYY",
  "M/D/YY",
  "M-D-YYYY",
  "M-D-YY",
  "YYYY-M-D",
];

const parseDecimal = (input: string): number | null => {
  if (input.trim() === "") return null;

  const numeric = Number(input.trim());

  return Number.isFinite(numeric) ? numeric : null;
};

const parseDateInput = (rawInput: string): string | null => {
  const trimmed = rawInput.trim();
  const parsed = DATE_INPUT_FORMATS.map((format) =>
    dayjs.utc(trimmed, format, true),
  ).find((date) => date.isValid());

  return parsed ? parsed.format("YYYY-MM-DD") : null;
};

const normalizeDecimal = (rawInput: string, stripPattern: RegExp) => {
  const decimal = parseDecimal(rawInput.trim().replace(stripPattern, ""));

  return decimal === null ? rawInput : decimal;
};

const normalizePercent = (rawInput: string) => {
  const decimal = parseDecimal(
    rawInput.trim().replace(/,/g, "").replace(/%$/, ""),
  );

  return decimal === null ? rawInput : decimal / 100;
};

const normalizeBoolean = (rawInput: string): boolean | string => {
  const input = rawInput.trim().toLocaleLowerCase();

  if (input === "true" || input === "yes" || input === "1") return true;
  if (input === "false" || input === "no" || input === "0") return false;

  return rawInput;
};

const normalizeTypedValue = (
  valueType: string | null,
  rawInput: string,
): OptimisticCellValue => {
  switch (valueType) {
    case "percent":
      return normalizePercent(rawInput);
    case "currency":
      return normalizeDecimal(rawInput, /[$,]/g);
    case "number":
      return normalizeDecimal(rawInput, /,/g);
    case "text":
      return rawInput;
    case "date":
      return parseDateInput(rawInput) ?? rawInput;
    case "boolean":
      return normalizeBoolean(rawInput);
    default:
      return null;
  }
};

// Match the API's literal edit normalization closely enough for optimistic
// display. The server remains authoritative and replaces raw_input/computed_value
// after recalculation returns.
export const normalizeOptimisticCellValue = (
  cell: IAiSpreadsheetCell,
  rawInput: string,
): OptimisticCellValue => {
  const trimmed = rawInput.trim();
  if (trimmed === "") return null;

  const typedValue = normalizeTypedValue(cell.value_type, rawInput);
  if (typedValue !== null) return typedValue;

  return /^[-+]?[\d,]*\.?\d+$/.test(trimmed)
    ? normalizeDecimal(rawInput, /,/g)
    : rawInput;
};
